import {
  createEmptyCanvasContent,
  parseCanvasContent,
} from "../canvas/canvasContent";

export const NOTE_DOCUMENT_KIND = "netpad.document";
export const NOTE_DOCUMENT_VERSION = 1;

export const STORED_NOTE_FORMATS = Object.freeze({
  CANVAS: "canvas",
  DOCUMENT: "document",
  TEXT: "text",
});

const HTML_TAG_PATTERN = /<[^>]*>/g;
const NON_BREAKING_SPACE_ENTITY_PATTERN = /&nbsp;/gi;
const NOTE_EMBED_TAG_PATTERN = /<(img|iframe|video)\b/i;

const isObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * @typedef {{ format: string, noteText: string, canvasContent: string }} StoredNoteDocument
 */

const createTextDocument = (noteText) => ({
  canvasContent: createEmptyCanvasContent(),
  format: STORED_NOTE_FORMATS.TEXT,
  noteText: String(noteText ?? ""),
});

const isValidCanvasContentString = (value) =>
  typeof value === "string" && parseCanvasContent(value) !== null;

const isDocumentEnvelope = (parsedJson) =>
  isObject(parsedJson) &&
  parsedJson.kind === NOTE_DOCUMENT_KIND &&
  parsedJson.version === NOTE_DOCUMENT_VERSION;

const parseDocumentEnvelope = (envelope) => ({
  canvasContent: isValidCanvasContentString(envelope.canvas)
    ? envelope.canvas
    : createEmptyCanvasContent(),
  format: STORED_NOTE_FORMATS.DOCUMENT,
  noteText: typeof envelope.note === "string" ? envelope.note : "",
});

/**
 * Reads any stored note payload — plain text, a canvas envelope, or a
 * combined document envelope — into one shape holding both sides.
 * @param {unknown} rawContent
 * @returns {StoredNoteDocument}
 */
export const parseStoredNoteContent = (rawContent) => {
  if (typeof rawContent !== "string") return createTextDocument("");

  if (isValidCanvasContentString(rawContent)) {
    return {
      canvasContent: rawContent,
      format: STORED_NOTE_FORMATS.CANVAS,
      noteText: "",
    };
  }

  const parsedJson = safeParseJson(rawContent);
  if (isDocumentEnvelope(parsedJson)) return parseDocumentEnvelope(parsedJson);

  return createTextDocument(rawContent);
};

/**
 * True when the note HTML renders something visible. Embeds (images, videos)
 * count as content even though they carry no text.
 * @param {string | null | undefined} noteText
 * @returns {boolean}
 */
export const hasVisibleNoteText = (noteText) => {
  const rawNote = String(noteText ?? "");
  if (NOTE_EMBED_TAG_PATTERN.test(rawNote)) return true;

  const plainText = rawNote
    .replace(HTML_TAG_PATTERN, "")
    .replace(NON_BREAKING_SPACE_ENTITY_PATTERN, " ");
  return plainText.trim() !== "";
};

const hasCanvasElements = (canvasContent) => {
  const parsedCanvas = parseCanvasContent(canvasContent);
  return (parsedCanvas?.snapshot.elements.length ?? 0) > 0;
};

/**
 * Picks the smallest storage format that keeps every populated side: plain
 * text when the canvas is empty, the canvas envelope alone when the note is
 * empty, and the combined document envelope when both hold content. Notes
 * that never touch the canvas therefore keep their legacy plain-string shape.
 * @param {{ canvasContent?: string, noteText?: string | null }} document
 * @returns {string}
 */
export const serializeStoredNoteContent = ({ canvasContent, noteText }) => {
  const rawNote = String(noteText ?? "");
  if (!hasCanvasElements(canvasContent)) return rawNote;
  if (!hasVisibleNoteText(rawNote)) return canvasContent;

  return JSON.stringify({
    kind: NOTE_DOCUMENT_KIND,
    version: NOTE_DOCUMENT_VERSION,
    note: rawNote,
    canvas: canvasContent,
  });
};
